import type { AppLocale } from "@/lib/i18n/locale";
import { supabase } from "@/lib/supabase/client";

export const LOCAL_PROFILE_PREFERENCES_STORAGE_KEY = "mypetroutine:profile-preferences:guest:v1";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type UserProfilePreferences = {
  locale: AppLocale;
  displayTimezone: string;
  localeSource: "device" | "user_settings";
  displayTimezoneUpdatedAt: string | null;
};

const DEFAULT_DISPLAY_TIMEZONE = "Asia/Seoul";

export function normalizeProfileLocale(value: unknown): AppLocale {
  return value === "en" ? "en" : "ko";
}

export function isValidDisplayTimezone(value: unknown): value is string {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }

  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

export function normalizeDisplayTimezone(value: unknown): string {
  return isValidDisplayTimezone(value) ? value : DEFAULT_DISPLAY_TIMEZONE;
}

export function mapUserProfilePreferences(row: {
  locale?: unknown;
  display_timezone?: unknown;
  locale_source?: unknown;
  display_timezone_updated_at?: unknown;
} | null | undefined): UserProfilePreferences {
  return {
    locale: normalizeProfileLocale(row?.locale),
    displayTimezone: normalizeDisplayTimezone(row?.display_timezone),
    localeSource: row?.locale_source === "user_settings" ? "user_settings" : "device",
    displayTimezoneUpdatedAt: typeof row?.display_timezone_updated_at === "string"
      ? row.display_timezone_updated_at
      : null,
  };
}

export function readLocalProfilePreferences(storage?: StorageLike): UserProfilePreferences | null {
  try {
    const raw = storage?.getItem(LOCAL_PROFILE_PREFERENCES_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return mapUserProfilePreferences(JSON.parse(raw) as {
      locale?: unknown;
      display_timezone?: unknown;
      locale_source?: unknown;
      display_timezone_updated_at?: unknown;
    });
  } catch {
    return null;
  }
}

export function writeLocalProfilePreferences(
  storage: StorageLike | undefined,
  preferences: Pick<UserProfilePreferences, "locale" | "displayTimezone">,
): UserProfilePreferences {
  const normalized = mapUserProfilePreferences({
    locale: preferences.locale,
    display_timezone: preferences.displayTimezone,
    locale_source: "user_settings",
    display_timezone_updated_at: new Date().toISOString(),
  });
  try {
    storage?.setItem(LOCAL_PROFILE_PREFERENCES_STORAGE_KEY, JSON.stringify({
      locale: normalized.locale,
      display_timezone: normalized.displayTimezone,
      locale_source: normalized.localeSource,
      display_timezone_updated_at: normalized.displayTimezoneUpdatedAt,
    }));
  } catch {
    // Keep the in-memory settings usable when browser storage is unavailable.
  }
  return normalized;
}

export async function fetchUserProfilePreferences(userId: string): Promise<UserProfilePreferences> {
  const { data, error } = await supabase
    .from("profiles")
    .select("locale, display_timezone, locale_source, display_timezone_updated_at")
    .eq("id", userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return mapUserProfilePreferences(data);
}

export async function updateUserProfilePreferences(params: {
  userId: string;
  locale: AppLocale;
  displayTimezone: string;
}) {
  const displayTimezone = normalizeDisplayTimezone(params.displayTimezone);
  if (!isValidDisplayTimezone(params.displayTimezone)) {
    throw new Error("표시 시간대를 확인해주세요.");
  }

  const { data, error } = await supabase
    .from("profiles")
    .update({
      locale: normalizeProfileLocale(params.locale),
      display_timezone: displayTimezone,
      locale_source: "user_settings",
      display_timezone_updated_at: new Date().toISOString(),
    })
    .eq("id", params.userId)
    .select("locale, display_timezone, locale_source, display_timezone_updated_at")
    .single();

  if (error) {
    throw error;
  }

  return mapUserProfilePreferences(data);
}
